import {
  Button,
  ButtonMode,
  ButtonSize,
  Chip,
  colors,
  Divider,
  Tooltip,
  TooltipPosition,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DocketCase } from '../../../../models/CompanyDockets';
import { DATE_FORMATS, formatDate } from '../../../../utils/dateUtils';
import styles from './DocketCaseInfo.module.scss';
import DocketAlertPopup from '../DocketAlert/DocketAlertPopup';
import useDocketAlertPreference from '../../../../hooks/docketAlerts/useDocketAlertPreference';
import { CompanyPageParams } from '../../../../models/CompanyPageParams';

const cx = classNames.bind(styles);
interface DocketCaseInfoProps {
  docketCase: DocketCase;
  viewRelatedCases: () => void;
}
const renderInfo = (heading: string, data: string | null | undefined, dataTestId: string, tooltip = false) => (
  <div className={cx('item')}>
    <span className={cx('heading')}>{heading}</span>
    {tooltip ? (
      <div className={cx('data')} data-testid={dataTestId}>
        <Tooltip position={TooltipPosition.TopCenter} content={data} disabled={!data}>
          <span>{data || '-'}</span>
        </Tooltip>
      </div>
    ) : (
      <span className={cx('data')} data-testid={dataTestId}>
        {data || '-'}
      </span>
    )}
  </div>
);

const DocketCaseInfo = ({ docketCase, viewRelatedCases }: DocketCaseInfoProps): ReactElement => {
  const { issuerId } = useParams() as CompanyPageParams;
  const issuerIdNumber = parseInt(issuerId, 10);

  const { docketPreference, selectDocketAlert } = useDocketAlertPreference(docketCase.id, issuerIdNumber);

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

  const renderChip = () => {
    if (!docketCase.defaultCase) {
      return null;
    }
    return (
      <Chip
        label="Key Case"
        color={colors['docket-keycase-font-color']}
        backgroundColor={colors['docket-keycase-bg-color']}
        dataTestId="key-case"
      />
    );
  };
  return (
    <div className={cx('body')} data-testid="docket-case-info">
      <div className={cx('header')}>
        <div className={cx('left-section')}>
          <div className={cx('info')} data-testid="docket-case-name">
            <Tooltip position={TooltipPosition.TopCenter} content={docketCase.name}>
              <h1 className={cx('title')}>{docketCase.name}</h1>
            </Tooltip>
            {renderChip()}
          </div>
        </div>
        <div className={cx('right-section')}>
          <DocketAlertPopup
            docketPreference={docketPreference}
            selectDocketAlert={selectDocketAlert}
            isOpen={isPopupOpen}
            setIsOpen={setIsPopupOpen}
            caseId={docketCase.id}
            companyId={issuerIdNumber}
          />
          <Button mode={ButtonMode.SECONDARY} size={ButtonSize.SMALL} onClick={viewRelatedCases}>
            View Related Cases
          </Button>
        </div>
      </div>
      <div className={cx('container')}>
        {renderInfo('Case #', docketCase.caseNumber, 'case-number', true)}
        {renderInfo('Case Type', docketCase.type, 'case-type')}
        {renderInfo('Court', docketCase.courtName, 'case-court', true)}
        {renderInfo('Judge', docketCase.judges.join(', '), 'case-judge', true)}
        {renderInfo('Date Filed', formatDate(docketCase.date, DATE_FORMATS.MONTH_ABBR_DATE, '-'), 'date-filed')}
      </div>
      <Divider />
    </div>
  );
};

export default DocketCaseInfo;
